// Parses the wiki's activity log (`wiki/log.md`) into typed entries for
// the /log page. `Wiki::LogAppender` owns the format: a `# Brain Log`
// heading, then one `YYYY-MM-DD HH:MM — text` line per entry (UTC,
// newest first, blank-line separated), then a trailing description and
// an HTML comment. Anything that isn't a stamped line is ignored, except
// a non-blank line glued directly under an entry, which continues it.
//
// Pure functions only — the view owns fetching and the clock.

export type LogKind = 'ingest' | 'delete' | 'lint' | 'synthesis' | 'query' | 'edit' | 'activity'

export interface BrainLogEntry {
  id: string
  at: Date
  text: string
  title: string
  detail: string
  kind: LogKind
}

export interface LogSegment {
  text: string
  // Set when the segment is a wiki page path (e.g. `concepts/foo.md`).
  path: string | null
}

export interface LogDay {
  key: string
  label: string
  entries: BrainLogEntry[]
}

// Label + tinted-pill classes per operation. Tones follow the design
// tokens: knowledge in (mint), removal (coral), nightly audit (amber,
// agent work), synthesis (violet), questions (blue), edits neutral.
export const LOG_KINDS: Record<LogKind, { label: string; tone: string }> = {
  ingest: { label: 'Ingest', tone: 'bg-brand-primary/15 text-brand-primary' },
  delete: { label: 'Delete', tone: 'bg-status-alert/15 text-status-alert' },
  lint: { label: 'Lint', tone: 'bg-factory/15 text-factory' },
  synthesis: { label: 'Synthesis', tone: 'bg-life/15 text-life' },
  query: { label: 'Query', tone: 'bg-attention/15 text-attention' },
  edit: { label: 'Edit', tone: 'bg-surface-hover text-ink-muted' },
  activity: { label: 'Activity', tone: 'bg-surface-hover text-ink-muted' },
}

const STAMPED_LINE = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})\s+[—-]\s+(.*)$/
// LLM entries sometimes arrive double-stamped ("2026-05-14 — Ingested …").
const LEADING_DATE = /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?\s*[—-]\s*/
const WIKI_PATH = /(?<![\w/.-])(?:[\w-]+\/)*[\w.-]+\.md\b/g
// Where a one-line entry splits into a headline and its detail.
const SPLIT_AT = /[:;] /
const MAX_TITLE_CHARS = 140

// The verb an entry opens with is the strongest signal; keywords
// anywhere in the line are the fallback. First match wins.
const LEADING_VERBS: Array<[RegExp, LogKind]> = [
  [/^ingest(?:ed|ing)?\b/i, 'ingest'],
  [/^(?:deleted|removed)\b/i, 'delete'],
  [/^(?:synthesi[sz]ed|synthesis)\b/i, 'synthesis'],
  [/^(?:lint|accepted lint|reconciled)\b/i, 'lint'],
  [/^(?:query|queried|answered)\b/i, 'query'],
  [/^(?:published|edited|renamed|updated|linked|created)\b/i, 'edit'],
]
const KEYWORDS: Array<[RegExp, LogKind]> = [
  [/\blint\b/i, 'lint'],
  [/\bsynthes/i, 'synthesis'],
  [/\bingest|\bsource-\d+\b/i, 'ingest'],
  [/\bquer(?:y|ied)\b/i, 'query'],
  [/\b(?:owner edit|edited)\b/i, 'edit'],
]

/** Which operation a log line records; 'activity' when nothing matches. */
export function classifyLogEntry(text: string): LogKind {
  const trimmed = text.trim()
  for (const [pattern, kind] of LEADING_VERBS) if (pattern.test(trimmed)) return kind
  for (const [pattern, kind] of KEYWORDS) if (pattern.test(trimmed)) return kind
  return 'activity'
}

/** Headline + detail: split at the first ": " / "; " when it comes early. */
export function splitLogText(text: string): { title: string; detail: string } {
  const match = SPLIT_AT.exec(text)
  if (!match || match.index === 0 || match.index > MAX_TITLE_CHARS) return { title: text, detail: '' }
  return { title: text.slice(0, match.index), detail: text.slice(match.index + 2).trim() }
}

/** Splits text into plain runs and wiki-page-path runs, in order. */
export function linkifyLogText(text: string): LogSegment[] {
  const segments: LogSegment[] = []
  let cursor = 0
  for (const match of text.matchAll(WIKI_PATH)) {
    const start = match.index ?? 0
    if (start > cursor) segments.push({ text: text.slice(cursor, start), path: null })
    segments.push({ text: match[0], path: match[0] })
    cursor = start + match[0].length
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), path: null })
  return segments
}

function buildEntry(id: string, date: string, time: string, raw: string): BrainLogEntry | null {
  const at = new Date(`${date}T${time}:00Z`)
  const text = raw.replace(LEADING_DATE, '').trim()
  if (Number.isNaN(at.getTime()) || !text) return null
  return { id, at, text, ...splitLogText(text), kind: classifyLogEntry(text) }
}

/** Every stamped entry in the log, newest first, exact duplicates dropped. */
export function parseBrainLog(markdown: string): BrainLogEntry[] {
  const raw: Array<{ date: string; time: string; text: string }> = []
  let open: { date: string; time: string; text: string } | null = null
  for (const line of markdown.split(/\r?\n/)) {
    const stamped = STAMPED_LINE.exec(line)
    if (stamped) {
      open = { date: stamped[1], time: stamped[2], text: stamped[3] }
      raw.push(open)
    } else if (!line.trim()) {
      open = null
    } else if (open && !line.startsWith('<!--')) {
      open.text = `${open.text} ${line.trim()}`
    }
  }
  const seen = new Set<string>()
  const entries: BrainLogEntry[] = []
  for (const row of raw) {
    const key = `${row.date} ${row.time} ${row.text}`
    if (seen.has(key)) continue
    seen.add(key)
    const entry = buildEntry(`log-${entries.length}`, row.date, row.time, row.text)
    if (entry) entries.push(entry)
  }
  return entries.toSorted((a, b) => b.at.getTime() - a.at.getTime())
}

function localDayKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** "Today", "Yesterday", else "Mon 3 Nov" (plus the year when it differs). */
export function logDayLabel(date: Date, now: Date): string {
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  if (localDayKey(date) === localDayKey(now)) return 'Today'
  if (localDayKey(date) === localDayKey(yesterday)) return 'Yesterday'
  const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }
  if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric'
  return date.toLocaleDateString('en-GB', options).replace(',', '')
}

/** Local "HH:MM" for an entry's row. */
export function logTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/** Consecutive entries grouped by local calendar day, order preserved. */
export function groupLogByDay(entries: BrainLogEntry[], now: Date): LogDay[] {
  const days: LogDay[] = []
  for (const entry of entries) {
    const key = localDayKey(entry.at)
    const last = days.at(-1)
    if (last && last.key === key) last.entries.push(entry)
    else days.push({ key, label: logDayLabel(entry.at, now), entries: [entry] })
  }
  return days
}

/**
 * Id of the newest entry the reader had already seen — the "Since your
 * last visit" divider sits above it. Null when there is no previous visit,
 * nothing is new, or everything is new (a divider at either end says nothing).
 */
export function firstSeenEntryId(entries: BrainLogEntry[], seenAt: string | null): string | null {
  if (!seenAt) return null
  const seen = new Date(seenAt).getTime()
  if (Number.isNaN(seen)) return null
  const index = entries.findIndex((entry) => entry.at.getTime() <= seen)
  return index > 0 ? entries[index].id : null
}

/** Entry counts per kind, for the filter chips. */
export function countLogKinds(entries: BrainLogEntry[]): Partial<Record<LogKind, number>> {
  const counts: Partial<Record<LogKind, number>> = {}
  for (const entry of entries) counts[entry.kind] = (counts[entry.kind] ?? 0) + 1
  return counts
}
